import React, { useEffect, useRef, useState } from "react";

// Vue « Guide » du dashboard coach : les vidéos tutos (public/guide/), rangées
// par thème. Chaque titre est l'action que la vidéo apprend à faire. L'ordre
// suit celui des écrans de fin, qui annoncent la vidéo suivante.
const SECTIONS = [
	{
		titre: "Démarrer",
		videos: [
			{ fichier: "inviter-un-eleve", titre: "Inviter un élève", duree: "0:46", resume: "Envoyer une invitation par email ou copier le lien, puis retrouver l'élève dans la liste." },
		],
	},
	{
		titre: "Séances",
		videos: [
			{ fichier: "creer-une-seance", titre: "Créer une séance pour un élève", duree: "1:00", resume: "Choisir la date, ajouter les exercices et les charges, fixer un RPE, puis enregistrer." },
			{ fichier: "planifier-des-seances", titre: "Planifier des séances sur plusieurs semaines", duree: "0:46", resume: "Partir d'un modèle, choisir la date de début, le nombre de semaines et les jours." },
			{ fichier: "affecter-un-modele-en-masse", titre: "Affecter un modèle à plusieurs élèves", duree: "0:50", resume: "Choisir un modèle, les élèves et les dates : toutes les séances sont créées d'un coup." },
		],
	},
	{
		titre: "Bibliothèque",
		videos: [
			{ fichier: "creer-un-modele", titre: "Créer un modèle et le ranger dans un dossier", duree: "1:06", resume: "Composer un modèle de séance réutilisable, puis le classer dans un dossier." },
			{ fichier: "creer-un-exercice", titre: "Créer un exercice personnalisé", duree: "0:50", resume: "Ajouter un exercice à ta bibliothèque : nom, description, repos et muscles travaillés." },
		],
	},
	{
		titre: "Programmes",
		videos: [
			{ fichier: "construire-un-programme", titre: "Construire un programme et l'affecter", duree: "1:18", resume: "Créer un cycle de plusieurs semaines, régler la progression, puis l'affecter à des élèves." },
			{ fichier: "programmer-la-progression", titre: "Programmer la progression en % du max", duree: "1:19", resume: "Fixer les reps et le pourcentage du max série par série, semaine après semaine." },
		],
	},
];

const VIDEOS = SECTIONS.flatMap((s) => s.videos);
const CLE_VUES = "gymnote_guide_vues";

const lireVues = () => {
	try {
		return JSON.parse(localStorage.getItem(CLE_VUES)) || [];
	} catch {
		return [];
	}
};

export default function CoachGuide() {
	const [courante, setCourante] = useState(VIDEOS[0].fichier);
	const [vues, setVues] = useState(lireVues);
	const lecteur = useRef(null);
	const lectureDemandee = useRef(false);

	const index = VIDEOS.findIndex((v) => v.fichier === courante);
	const video = VIDEOS[index];
	const suivante = VIDEOS[index + 1];

	// Au changement de vidéo, la lecture ne démarre que si c'est un clic qui
	// l'a demandée : à l'ouverture de l'onglet, rien ne se lance tout seul.
	useEffect(() => {
		if (!lectureDemandee.current || !lecteur.current) return;
		lectureDemandee.current = false;
		lecteur.current.play().catch(() => {});
	}, [courante]);

	const ouvrir = (fichier) => {
		lectureDemandee.current = true;
		setCourante(fichier);
		// Sur mobile, le lecteur est au-dessus de la liste : on y remonte.
		lecteur.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
	};

	const marquerVue = () => {
		if (vues.includes(courante)) return;
		const maj = [...vues, courante];
		setVues(maj);
		try {
			localStorage.setItem(CLE_VUES, JSON.stringify(maj));
		} catch {}
	};

	return (
		<div className="coach-guide-page">
			<div className="coach-programs-head">
				<div>
					<h2 style={{ margin: 0, fontSize: 22 }}>Guide</h2>
					<div style={{ color: "var(--ink-soft)", fontSize: 13, marginTop: 4 }}>
						Des vidéos courtes pour chaque action du dashboard · {vues.length}/{VIDEOS.length} vues
					</div>
				</div>
			</div>

			<div className="coach-guide-layout">
				<div className="coach-guide-player">
					<video
						key={video.fichier}
						ref={lecteur}
						className="coach-guide-video"
						src={`/guide/${video.fichier}.mp4`}
						controls
						playsInline
						preload="metadata"
						onEnded={marquerVue}
					/>
					<div className="coach-guide-player-info">
						<div className="coach-guide-player-step">
							Vidéo {index + 1} sur {VIDEOS.length} · {video.duree}
						</div>
						<h3 className="coach-guide-player-title">{video.titre}</h3>
						<p className="coach-guide-player-desc">{video.resume}</p>
						{suivante && (
							<button className="mens-btn ghost" onClick={() => ouvrir(suivante.fichier)}>
								Suivante : {suivante.titre} →
							</button>
						)}
					</div>
				</div>

				<nav className="coach-guide-list" aria-label="Vidéos du guide">
					{SECTIONS.map((section) => (
						<div key={section.titre} className="coach-guide-section">
							<div className="coach-guide-section-title">{section.titre}</div>
							{section.videos.map((v) => {
								const active = v.fichier === courante;
								const vue = vues.includes(v.fichier);
								return (
									<button
										key={v.fichier}
										type="button"
										className={`coach-guide-item ${active ? "active" : ""}`}
										onClick={() => ouvrir(v.fichier)}
										aria-current={active ? "true" : undefined}
									>
										<span className={`coach-guide-item-icon ${vue ? "vue" : ""}`} aria-hidden="true">
											{vue ? (
												<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
													<polyline points="20 6 9 17 4 12" />
												</svg>
											) : (
												<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
													<path d="M7 4v16l13-8z" />
												</svg>
											)}
										</span>
										<span className="coach-guide-item-title">{v.titre}</span>
										<span className="coach-guide-item-duration">{v.duree}</span>
									</button>
								);
							})}
						</div>
					))}
				</nav>
			</div>
		</div>
	);
}
